import React, { FC, useState } from 'react';
import { TextField, Box, Typography, useTheme, Switch, FormHelperText } from '@mui/material';

import { BuilderComponentProps, useNode } from '@mnz/builder';
import Uploader from '../../common/uploader/Uploader';
import { boxCss, labelCss, descriptionCss } from '../../common/style';
import MultipleUploader from '../../common/uploader/MulipleUploader';
import { px2rem } from '../../core';

export interface BasicInfoSettingProps extends BuilderComponentProps {
    uploadImage: any;
    image?: string;
    coverImage?: string;
    hiddenBasicInfoItems: Partial<Record<HiddenBasicKey, boolean>>;
}

export type HiddenBasicKey = 'LOGO' | 'BUSINESS_NAME' | 'BUSINESS_DESCRIPTION' | 'COVER_IMAGE';

export const BasicInfoSetting: FC<BasicInfoSettingProps> = ({ uploadImage }) => {
    const [isError, setIsError] = useState(false);
    const theme = useTheme();
    const {
        actions: { setAccount, setProp, setDesignSettings },
        props: { account, designSettings, hiddenBasicInfoItems, image },
    } = useNode((node) => {
        return {
            hidden: node.data.hidden,
            props: node.data.props as unknown as BasicInfoSettingProps,
        };
    });

    return (
        <div>
            <Box mt={1} mb={2} sx={boxCss(theme)}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography sx={labelCss()}>Business Name</Typography>
                    <Switch
                        checked={
                            hiddenBasicInfoItems && typeof hiddenBasicInfoItems.BUSINESS_NAME !== 'undefined'
                                ? !hiddenBasicInfoItems.BUSINESS_NAME
                                : true
                        }
                        onChange={(e) => {
                            setProp<BasicInfoSettingProps>((props) => {
                                if (typeof hiddenBasicInfoItems === 'undefined') {
                                    props.hiddenBasicInfoItems = {};
                                }
                                if (typeof hiddenBasicInfoItems?.BUSINESS_NAME === 'undefined') {
                                    props.hiddenBasicInfoItems.BUSINESS_NAME = true;
                                }
                                props.hiddenBasicInfoItems.BUSINESS_NAME = !e.target.checked;
                            });
                        }}
                    />
                </Box>
                <TextField
                    sx={{ marginTop: 1.5 }}
                    defaultValue={account.name}
                    placeholder="Business Name"
                    fullWidth={true}
                    onChange={(e) => {
                        setIsError(e.target.value === '');
                        if (e.target.value !== '') {
                            setAccount({ name: e.target.value });
                        }
                    }}
                />
                {isError && <FormHelperText error={true}>Business name is required.</FormHelperText>}
            </Box>

            <Box mb={2} sx={boxCss(theme)}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography sx={labelCss()}>Logo</Typography>
                    <Switch
                        checked={
                            hiddenBasicInfoItems && typeof hiddenBasicInfoItems.LOGO !== 'undefined'
                                ? !hiddenBasicInfoItems.LOGO
                                : true
                        }
                        onChange={(e) => {
                            setProp<BasicInfoSettingProps>((props) => {
                                if (typeof hiddenBasicInfoItems === 'undefined') {
                                    props.hiddenBasicInfoItems = {};
                                }
                                if (typeof hiddenBasicInfoItems?.LOGO === 'undefined') {
                                    props.hiddenBasicInfoItems.LOGO = true;
                                }
                                props.hiddenBasicInfoItems.LOGO = !e.target.checked;
                            });
                        }}
                    />
                </Box>

                <MultipleUploader
                    css={{ marginTop: px2rem(20) }}
                    imageList={account?.logoList}
                    value={image}
                    onSelect={(image) => {
                        setProp<BasicInfoSettingProps>((props) => (props.image = image));
                    }}
                    onSuccess={(url) => {
                        setAccount({ logoList: [url, ...(account.logoList || [])] });
                        setProp<BasicInfoSettingProps>((props) => (props.image = url));
                    }}
                    onRemove={(removedImg) => {
                        const list = account.logoList.filter((i) => i !== removedImg);
                        setAccount({ logoList: list });
                        setTimeout(() => {
                            if (list.length === 0) {
                                setProp<BasicInfoSettingProps>((props) => (props.image = null));
                            } else if (image === removedImg) {
                                setProp<BasicInfoSettingProps>((props) => (props.image = list[0]));
                            }
                        });
                    }}
                    uploadImage={uploadImage}
                />
            </Box>

            <Box mb={2} sx={boxCss(theme)}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography sx={labelCss()}>Business Description</Typography>
                    <Switch
                        checked={
                            hiddenBasicInfoItems && typeof hiddenBasicInfoItems.BUSINESS_DESCRIPTION !== 'undefined'
                                ? !hiddenBasicInfoItems.BUSINESS_DESCRIPTION
                                : true
                        }
                        onChange={(e) => {
                            setProp<BasicInfoSettingProps>((props) => {
                                if (typeof hiddenBasicInfoItems === 'undefined') {
                                    props.hiddenBasicInfoItems = {};
                                }
                                if (typeof hiddenBasicInfoItems?.BUSINESS_DESCRIPTION === 'undefined') {
                                    props.hiddenBasicInfoItems.BUSINESS_DESCRIPTION = true;
                                }
                                props.hiddenBasicInfoItems.BUSINESS_DESCRIPTION = !e.target.checked;
                            });
                        }}
                    />
                </Box>
                <p css={descriptionCss(theme)}>Maximum 80 characters</p>

                <TextField
                    sx={{ marginTop: 1 }}
                    defaultValue={account.about}
                    placeholder="Add a description"
                    fullWidth={true}
                    rows={4}
                    multiline={true}
                    inputProps={{
                        maxLength: 80,
                    }}
                    onChange={(e) => setAccount({ about: e.target.value })}
                />
            </Box>

            <Box mb={2} sx={boxCss(theme)}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography sx={labelCss()}>Cover Image</Typography>
                    <Switch
                        checked={
                            hiddenBasicInfoItems && typeof hiddenBasicInfoItems.COVER_IMAGE !== 'undefined'
                                ? !hiddenBasicInfoItems.COVER_IMAGE
                                : true
                        }
                        onChange={(e) => {
                            setProp<BasicInfoSettingProps>((props) => {
                                if (typeof hiddenBasicInfoItems === 'undefined') {
                                    props.hiddenBasicInfoItems = {};
                                }
                                if (typeof hiddenBasicInfoItems?.COVER_IMAGE === 'undefined') {
                                    props.hiddenBasicInfoItems.COVER_IMAGE = true;
                                }
                                props.hiddenBasicInfoItems.COVER_IMAGE = !e.target.checked;
                            });
                        }}
                    />
                </Box>
                <p css={descriptionCss(theme)}>Maximum file size 2MB</p>

                <Uploader
                    css={{ height: '100px', marginTop: '8px' }}
                    imgCss={{ objectFit: 'cover' }}
                    value={designSettings.coverImage}
                    onSuccess={(url) => setDesignSettings({ coverImage: url })}
                    onRemove={() => setDesignSettings({ coverImage: '' })}
                    uploadImage={uploadImage}
                    maxSize={2 * 1024 * 1024}
                />
            </Box>
        </div>
    );
};
